<template>
  <div
    id="canvasWrapper"
    ref="canvasWrapper"
    @mouseenter="mouseEnter"
    @mouseleave="mouseLeave"
    @wheel.prevent="zooming"
  >
    <canvas
      ref="capustaCanvas"
      v-hammer:tap="hammerTap"
      v-hammer:pan.horizontal="hammerPan"
      v-hammer:pinch="hammerPinchMove"
      v-hammer:pinchin="hammerPinchIn"
      v-hammer:pinchout="hammerPinchOut"
      v-hammer:panstart="hammerPanStart"
      v-hammer:panend="hammerPanEnd"
      :width="width"
      :height="height"
    ></canvas>

    <div v-if="!DEVICE.isMobile" id="controllerSwitch">
      <a
        v-for="(el, idx) in chartData"
        :key="idx"
        :class="{ 'selected': el.show }"
        @click="controllerSwitchClickHandler(el.name)"
      >
        <em :style="{ background: el.colorArc }"></em>
        <span>{{ $t(el.name) }}</span>
      </a>
    </div>

    <div
      v-show="showHint"
      id="hint"
      :style="{
        left: !DEVICE.isMobile ? `${hint.x - 103}px` : 0,
        top: `${hint.y}px`,
        right: DEVICE.isMobile && 0,
        background: COLORS.controllerBg,
      }"
    >
      <div>
        <div v-for="(item, idx) in SLICED_MAIN" :key="idx">
          <template v-if="item.length">
            <h4 :style="{ color: chartData[idx].colorArc }">
              {{ $t(chartData[idx].name) }}
              <em
                v-if="chartData[idx].name === $t('common.balance')"
                v-text="displayAmount(item[hint.index].y)"
              ></em>
            </h4>

            <template v-if="chartData[idx].name === $t('common.balance')">
              <section>
                <span>{{ $t('common.outputs') }}:</span>
                <em>{{ $t('common.pieces', { count: item[hint.index].count }) }}</em>
                <br/>
              </section>

              <section>
                <span>{{ $t('for.amount') }}:</span>
                <em v-text="displayAmount(item[hint.index].payout)"></em>
              </section>
            </template>

            <template v-else>
              <section>
                <span>{{ $t('of.transfers') }}:</span>
                <em>{{ $t('common.pieces', { count: item[hint.index].count }) }}</em>
                <br/>
              </section>

              <section>
                <span>{{ $t('for.amount') }}:</span>
                <em v-text="displayAmount(item[hint.index].y)"></em>
              </section>
            </template>
          </template>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';

import { formatAmount } from 'lib/utils/amount';

import { debounce } from '@/services/mixins';

import { chartSystemMethods } from './chartMixin';

const requestAnimationFrame = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;

const MARGIN = [15, 30, 25, 75];
const CONTROLLER_HEIGHT = 4;
const CONTROLLER_BORDER_RADIUS = 4;
const SEPERATOR_COUNT_X = 20;
const SEPERATOR_COUNT_Y = 7; // элементов показываем по У

const COLORS = {
  verticalLines: '#3a3a4e',
  hoverdLine: '#4b4b7b',
  text: '#93939c',
  controllerBg: '#1e1c2f',
  controllerBtn: '#8e8b92',
};

const FONT = '14px Montserrat';
const ARC_WIDTH = 3;

export default {
  name: 'Chart',
  mixins: [
    chartSystemMethods,
    debounce,
  ],
  props: {
    data: {
      type: Array,
      required: true,
    },
  },
  mounted() {
    window.addEventListener('resize', this.resized);
    window.addEventListener('mousemove', this.mouseMove);
    window.addEventListener('click', this.windowClick);
    window.addEventListener('scroll', this.windowScroll);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resized);
    window.removeEventListener('mousemove', this.mouseMove);
    window.removeEventListener('click', this.windowClick);
    window.removeEventListener('scroll', this.windowScroll);
  },
  data() {
    return {
      chartData: [],
      ctx: undefined,
      animated: false, // отрисовка включена
      width: 1000,
      height: 260,

      CURRENT_SEPERATOR_X: SEPERATOR_COUNT_X,
      CURRENT_SEPERATOR_Y: SEPERATOR_COUNT_Y,
      MARGIN_LEFT: 70,
      MARGIN_RIGHT: 30,
      GRAPH_HEIGHT: 200,
      COLORS,

      events: {
        mouseOver: false,
        graphPanning: false,
        controllerBtnPanning: false,
        pinching: false,
        x: 0, // позиция мышки при наведении по X
        y: 0, // позиция мышки при наведении по Y
      },

      hint: {
        x: undefined,
        y: MARGIN[0] - 100,
        index: 0,
      },

      firstElement: 0, // %
      lastELement: 100, // %

      panVelocity: 0.7,
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    showHint() {
      if (this.DEVICE.isMobile) {
        return this.hint.x && !this.events.controllerBtnPanning && !this.events.graphPanning && !this.events.pinching;
      }

      return !this.events.controllerBtnPanning && !this.events.graphPanning && this.hint.x && this.events.mouseOver;
    },
    getChartLength() {
      return this.chartData[0].data.length;
    },
    getStep() { // pxls
      return (this.getLastElement - this.getFirstElement) / this.CURRENT_SEPERATOR_X;
    },
    SLICED_MAIN() {
      let result = [];

      this.chartData.forEach((element, idx) => {
        result.push([]);

        if (typeof element.show !== 'undefined' && !element.show) {
          return;
        }

        for (let el = this.getFirstElement, i = 1; i <= this.CURRENT_SEPERATOR_X; el += this.getStep, i++) {
          if (i === this.CURRENT_SEPERATOR_X) {
            result[idx].push(this.chartData[idx].data[Math.floor(this.getLastElement - 1)]);
            continue;
          }

          result[idx].push(this.chartData[idx].data[Math.floor(el)]);
        }
      });
      return result;
    },
    SLICED_MAIN_EXIST() {
      return this.SLICED_MAIN.find(el => el.length);
    },
    maxY() {
      let list = [];

      this.SLICED_MAIN.forEach(el => {
        list.push(Math.max.apply(null, el.map(el => el.y)));
      });

      const result = Math.max.apply(null, list);

      return result === 0 ? 1 : result;
    },
    getGraphWidth() { // pxls
      return this.width - this.MARGIN_RIGHT - this.MARGIN_LEFT;
    },
    getCellWidth() { // pxls
      return this.getGraphWidth / (this.CURRENT_SEPERATOR_X - 1);
    },
    getFirstElement() { // element
      return this.getChartLength * this.firstElement / 100;
    },
    getLastElement() { // element
      return this.getChartLength * this.lastELement / 100;
    },
    getControllerX() { // pxls
      // eslint-disable-next-line no-mixed-operators
      return this.getGraphWidth * this.firstElement / 100 + this.MARGIN_LEFT;
    },
    getControllerY() { // pxls
      return MARGIN[0] + this.GRAPH_HEIGHT + 40;
    },
    getControllerWidth() { // pxls
      // eslint-disable-next-line no-mixed-operators
      const rightX = this.getGraphWidth * this.lastELement / 100 + this.MARGIN_LEFT;

      return rightX - this.getControllerX;
    },
    ...mapState(['DEVICE', 'PROJECT_INFO']),
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        this.chartData = val;
        this.$nextTick(this.initLoad);
      },
    },
    width(value) {
      this.getResponsive(value);
    },
  },
  methods: {
    displayAmount(amount) {
      return formatAmount(amount, this.currency);
    },
    initLoad() {
      this.calculateCanvasWidth();
      this.getResponsive(this.width);

      this.firstElement = (this.getChartLength - this.CURRENT_SEPERATOR_X) * 100 / this.getChartLength;
      this.ctx = this.$refs.capustaCanvas.getContext('2d');

      this.$nextTick(() => {
        this.animated = true;
        requestAnimationFrame(this.draw);
      });
    },

    draw() {
      this.ctx.clearRect(0, 0, this.width, this.height);
      this.drawElements();

      if (this.animated) {
        requestAnimationFrame(this.draw);
      }
    },
    drawElements() {
      this.drawGraphs();
      this.drawLinesAndDateAndPoints();

      if (this.DEVICE.isMobile) {
        this.drowTextAmoountMobile();
      } else {
        this.drowTextAmoount();
      }

      this.drawHints();
      this.drawControllers();
    },
    drawGraphs() {
      this.SLICED_MAIN.forEach((el, index) => {
        if (!el.length) {
          return;
        }

        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.lineWidth = 2;

        let gradient = this.ctx.createLinearGradient(0, 0, this.getGraphWidth, this.GRAPH_HEIGHT);
        gradient.addColorStop(0, this.chartData[index].gradientLine[0]);
        gradient.addColorStop(1, this.chartData[index].gradientLine[1]);

        this.ctx.strokeStyle = gradient;
        this.ctx.moveTo(this.MARGIN_LEFT, this.getAmountYPosition(el[0].y));

        for (let i = 1; i < el.length; i++) {
          // eslint-disable-next-line no-mixed-operators
          this.drawBazierCurve((i - 1) * this.getCellWidth + this.MARGIN_LEFT,
            this.getAmountYPosition(el[i - 1].y),
            // eslint-disable-next-line no-mixed-operators
            i * this.getCellWidth + this.MARGIN_LEFT,
            this.getAmountYPosition(el[i].y)
          );
        }

        this.ctx.stroke();
        this.ctx.closePath();
        this.ctx.restore();

        // рисуем заливку градиентом
        this.ctx.save();
        this.ctx.beginPath();

        let gradientFill = this.ctx.createLinearGradient(this.getGraphWidth / 2, 0, this.getGraphWidth / 2, this.GRAPH_HEIGHT);
        gradientFill.addColorStop(0, this.chartData[index].gradientFill[0]);
        gradientFill.addColorStop(1, this.chartData[index].gradientFill[1]);

        this.ctx.fillStyle = gradientFill;
        this.ctx.moveTo(this.MARGIN_LEFT, this.getAmountYPosition(el[0].y));

        for (let i = 1; i < el.length; i++) {
          // eslint-disable-next-line no-mixed-operators
          this.drawBazierCurve((i - 1) * this.getCellWidth + this.MARGIN_LEFT,
            this.getAmountYPosition(el[i - 1].y),
            // eslint-disable-next-line no-mixed-operators
            i * this.getCellWidth + this.MARGIN_LEFT,
            this.getAmountYPosition(el[i].y)
          );

          if (i + 1 === el.length) {
            // eslint-disable-next-line no-mixed-operators
            this.ctx.lineTo(i * this.getCellWidth + this.MARGIN_LEFT, this.GRAPH_HEIGHT);
            this.ctx.lineTo(this.MARGIN_LEFT, this.GRAPH_HEIGHT);
            this.ctx.lineTo(this.MARGIN_LEFT, this.getAmountYPosition(el[0].y));
          }
        }

        this.ctx.fill();
        this.ctx.closePath();
        this.ctx.restore();
      });
    },
    drawControllers() {
      this.createRoundedRect(
        this.MARGIN_LEFT,
        this.getControllerY,
        this.getGraphWidth,
        CONTROLLER_HEIGHT,
        CONTROLLER_BORDER_RADIUS,
        COLORS.controllerBg,
        COLORS.controllerBg
      );

      this.createRoundedRect(
        this.getControllerX,
        this.getControllerY,
        this.getControllerWidth,
        CONTROLLER_HEIGHT,
        CONTROLLER_BORDER_RADIUS,
        COLORS.controllerBtn,
        COLORS.controllerBtn
      );
    },
    // eslint-disable-next-line complexity
    drawHints() {
      if (this.events.graphPanning || this.events.controllerBtnPanning || this.events.x < this.MARGIN_LEFT || this.events.x > this.getGraphWidth + this.MARGIN_LEFT || this.events.y > this.GRAPH_HEIGHT + MARGIN[0]) {
        return;
      }

      const factorX = this.DEVICE.isMobile ? this.getCellWidth * 0.5 : this.getCellWidth * 0.35;
      let resultX = undefined;

      for (let i = 0; i < this.SLICED_MAIN_EXIST.length; i++) {
        // eslint-disable-next-line no-mixed-operators
        let seperatorX = this.getCellWidth * i + this.MARGIN_LEFT;

        if (this.events.x <= seperatorX + factorX && this.events.x >= seperatorX - factorX) {
          resultX = seperatorX;

          if (this.DEVICE.isMobile && this.hint.x && this.hint.index === i) {
            resultX = undefined;
          }

          this.hint.index = i;
          break;
        }
      }

      this.hint.x = resultX ? resultX : undefined;

      if (!this.hint.x) {
        return;
      }

      this.createLine(
        { x: Math.round(resultX), y: MARGIN[0] },
        { x: Math.round(resultX), y: this.GRAPH_HEIGHT + MARGIN[0] },
        null,
        COLORS.hoverdLine,
        3
      );

      this.createCustomBoxShadow(
        resultX - this.getCellWidth,
        MARGIN[0],
        this.getCellWidth,
        this.GRAPH_HEIGHT,
        'rgba(0, 0, 0, .2)'
      );
    },
    drawLinesAndDateAndPoints() {
      this.SLICED_MAIN_EXIST.forEach((el, i) => {
        this.createText(
          // eslint-disable-next-line no-mixed-operators
          this.getCellWidth * i + this.MARGIN_LEFT,
          this.GRAPH_HEIGHT + MARGIN[0] + 25,
          this.formatDate(el.x),
          COLORS.text,
          FONT
        );

        this.createLine(
          // eslint-disable-next-line no-mixed-operators
          { x: this.getCellWidth * i + this.MARGIN_LEFT, y: MARGIN[0] },
          // eslint-disable-next-line no-mixed-operators
          { x: this.getCellWidth * i + this.MARGIN_LEFT, y: this.GRAPH_HEIGHT + MARGIN[0] },
          null,
          COLORS.verticalLines
        );
      });

      this.SLICED_MAIN.forEach((el, index) => {
        for (let i = 0; i < el.length; i++) {
          this.createArc(
            // eslint-disable-next-line no-mixed-operators
            i * this.getCellWidth + this.MARGIN_LEFT,
            this.getAmountYPosition(el[i].y),
            ARC_WIDTH,
            this.chartData[index].colorArc,
            2
          );
        }
      });
    },
    drowTextAmoount() {
      const cellHeight = Math.round((this.GRAPH_HEIGHT + MARGIN[0]) / (this.CURRENT_SEPERATOR_Y + 1));
      const stepAmount = this.maxY / this.CURRENT_SEPERATOR_Y;

      for (let i = 0; i <= this.CURRENT_SEPERATOR_Y; i++) {
        this.createText(
          this.MARGIN_LEFT - 15,
          // eslint-disable-next-line no-mixed-operators
          (this.GRAPH_HEIGHT + MARGIN[0]) - (cellHeight * i + 15),
          this.pacanFormatAmount(i * stepAmount, this.currency),
          COLORS.text,
          FONT,
          'right',
          'top'
        );
      }
    },
    drowTextAmoountMobile() {
      // eslint-disable-next-line no-mixed-operators
      const cellHeight = Math.round((this.GRAPH_HEIGHT + MARGIN[0]) / this.CURRENT_SEPERATOR_Y + 1) - 5;
      const stepAmount = this.maxY / this.CURRENT_SEPERATOR_Y;

      for (let i = 0; i <= this.CURRENT_SEPERATOR_Y; i++) {
        this.createText(
          this.width / 2,
          // eslint-disable-next-line no-mixed-operators
          (this.GRAPH_HEIGHT + MARGIN[0]) - (cellHeight * i),
          this.pacanFormatAmount(i * stepAmount, this.currency),
          COLORS.text,
          FONT,
          'center',
          'middle'
        );
      }
    },

    getAmountYPosition(value) {
      // eslint-disable-next-line no-mixed-operators
      const res = this.GRAPH_HEIGHT - this.GRAPH_HEIGHT * value / this.maxY;

      return res + MARGIN[0];
    },
    ///// events
    mouseMove(e) {
      if (this.DEVICE.isMobile) {
        return;
      }

      if (e.target.nodeName === 'CANVAS') {
        this.events.x = e.offsetX;
        this.events.y = e.offsetY;
        return;
      }

      this.events.x = e.clientX - this.$refs.canvasWrapper.getBoundingClientRect().left;
      this.events.y = e.clientY - this.$refs.canvasWrapper.getBoundingClientRect().top;
    },
    mouseEnter() {
      if (this.DEVICE.isMobile) {
        return;
      }

      this.events.mouseOver = true;
      this.animated = true;

      requestAnimationFrame(this.draw);
    },
    mouseLeave() {
      if (this.DEVICE.isMobile) {
        return;
      }

      this.events.mouseOver = false;

      if (!this.events.graphPanning && !this.events.controllerBtnPanning) {
        this.animated = false;
      }
    },
    // eslint-disable-next-line complexity
    hammerPan(e) {
      let direction = e.type;

      if (this.DEVICE.isMobile) {
        this.events.x = e.changedPointers[0].offsetX;
        this.events.y = e.changedPointers[0].offsetY;

        this.getIsOverStates();
      }

      if (this.events.graphPanning) {
        if (direction === 'panleft') {
          if (this.lastELement + this.panVelocity > 100) {
            return this.lastELement = 100;
          }

          this.firstElement += this.panVelocity;
          this.lastELement += this.panVelocity;
        } else if (direction === 'panright') {
          if (this.firstElement - this.panVelocity < 0) {
            return this.firstElement = 0;
          }

          this.firstElement -= this.panVelocity;
          this.lastELement -= this.panVelocity;
        }
      }

      if (this.events.controllerBtnPanning) {
        // eslint-disable-next-line no-mixed-operators
        const x = this.events.x - this.MARGIN_LEFT - this.getControllerWidth / 2;
        const diff = this.lastELement - this.firstElement;
        const firstElement = x * 100 / this.getGraphWidth;
        const lastELement = firstElement + diff;

        if (firstElement < 0) {
          return;
        }
        if (lastELement > 100) {
          return;
        }

        this.firstElement = firstElement;
        this.lastELement = lastELement;
      }
    },
    zooming(e) {
      const direction = this.checkDirectionZoom(e.deltaY);

      if (direction === 'up') { // сужаем
        if (this.getStep <= 1) {
          return;
        }

        this.firstElement += this.panVelocity;
      } else if (direction === 'down') { // расширяем
        // eslint-disable-next-line no-unused-expressions
        this.firstElement - this.panVelocity > 0 ? this.firstElement -= this.panVelocity : this.firstElement = 0;

        if (this.firstElement === 0) {
          // eslint-disable-next-line no-unused-expressions
          this.lastELement + this.panVelocity < 100 ? this.lastELement += this.panVelocity : this.lastELement = 100;
        }
      }
    },
    hammerPinchMove(e) {
      const direction = this.checkDirectionPinch(e.scale);

      if (direction === 'down') { // сужаем
        if (this.getStep <= 1) {
          return;
        }

        this.firstElement += this.panVelocity;
      } else if (direction === 'up') { // расширяем
        // eslint-disable-next-line no-unused-expressions
        this.firstElement - this.panVelocity > 0 ? this.firstElement -= this.panVelocity : this.firstElement = 0;

        if (this.firstElement === 0) {
          // eslint-disable-next-line no-unused-expressions
          this.lastELement + this.panVelocity < 100 ? this.lastELement += this.panVelocity : this.lastELement = 100;
        }
      }
    },
    getIsOverStates() {
      const factor = 20;
      // eslint-disable-next-line no-mixed-operators
      const onControllerBtn = this.isOver(this.events.x, this.events.y, this.getControllerX - factor, this.getControllerY - factor, this.getControllerWidth + factor * 2, CONTROLLER_HEIGHT + factor * 2);

      const onGraph = this.isOver(
        this.events.x,
        this.events.y,
        this.MARGIN_LEFT,
        MARGIN[0],
        this.getGraphWidth,
        this.GRAPH_HEIGHT
      );

      this.events.controllerBtnPanning = onControllerBtn ? true : false;
      this.events.graphPanning = onGraph ? true : false;
    },
    hammerPanStart(e) {
      this.getIsOverStates();
      document.body.style.cursor = 'move';

      if (e.pointerType === 'mouse') {
        return;
      }

      this.animated = true;
      requestAnimationFrame(this.draw);
    },
    hammerPanEnd(e) {
      this.events.controllerBtnPanning = false;
      this.events.graphPanning = false;
      document.body.style.cursor = '';

      if (e.pointerType === 'mouse') {
        return;
      }

      this.animated = false;
    },
    hammerPinchIn() {
      this.events.pinching = true;
      this.animated = true;
      requestAnimationFrame(this.draw);
    },
    hammerPinchOut() {
      this.events.pinching = false;
      this.animated = false;
    },
    hammerTap(e) {
      if (e.pointerType === 'mouse') {
        return;
      }

      this.events.x = e.changedPointers[0].offsetX;
      this.events.y = e.changedPointers[0].offsetY;
      requestAnimationFrame(this.draw);
    },
    calculateCanvasWidth() {
      const w = this.$refs.canvasWrapper.offsetWidth;

      if (!w || (typeof this._oldW !== 'undefined' && this._oldW === w)) {
        return;
      }

      this.width = w;
      this._oldW = w;
      requestAnimationFrame(this.draw);
    },
    setCanvasParams(seperatorX, seperatorY, height, graphHeight) {
      this.CURRENT_SEPERATOR_X = seperatorX;
      this.CURRENT_SEPERATOR_Y = seperatorY;
      this.height = height;
      this.GRAPH_HEIGHT = graphHeight;

      if (this.DEVICE.isMobile) {
        this.MARGIN_LEFT = 18;
        this.MARGIN_RIGHT = 18;
        return;
      }

      this.MARGIN_LEFT = MARGIN[3];
      this.MARGIN_RIGHT = MARGIN[1];
    },
    // eslint-disable-next-line complexity
    getResponsive(value) {
      if (value > 1024) {
        this.setCanvasParams(this.getChartLength < 20 ? this.getChartLength : 20, SEPERATOR_COUNT_Y, 250, 185);
      } else if (value <= 1024 && value > 800) {
        this.setCanvasParams(this.getChartLength < 15 ? this.getChartLength : 15, SEPERATOR_COUNT_Y, 250, 185);
      } else if (value <= 800 && value > 500) {
        this.setCanvasParams(this.getChartLength < 12 ? this.getChartLength : 12, SEPERATOR_COUNT_Y, 230, 170);
      } else if (value <= 500 && value > 420) {
        this.setCanvasParams(this.getChartLength < 7 ? this.getChartLength : 7, 5, 220, 160);
      } else {
        this.setCanvasParams(this.getChartLength < 7 ? this.getChartLength : 7, 4, 190, 130);
      }
    },
    resized() {
      this.debounce(this.calculateCanvasWidth.bind(this), 200);
    },
    toggleMobileHintByScroll() {
      const rect = this.$refs.capustaCanvas.getBoundingClientRect();

      if (rect.top < 0) {
        this.hint.x = undefined;
      }
    },
    windowClick(e) {
      if (!this.DEVICE.isMobile || !this.showHint) {
        return;
      }

      const canvasRect = this.$refs.capustaCanvas.getBoundingClientRect();
      const [x, y, canvasX, canvasY] = [e.pageX, e.pageY, canvasRect.left, canvasRect.top];

      if (!this.isOver(x, y, canvasX, canvasY, this.getGraphWidth, this.GRAPH_HEIGHT + this.MARGIN_RIGHT)) {
        this.hint.x = undefined;
      }
    },
    windowScroll() {
      if (!this.DEVICE.isMobile) {
        return;
      }

      this.debounce(this.toggleMobileHintByScroll.bind(this), 100);
    },
    controllerSwitchClickHandler(name) {
      const anotherElements = this.chartData.filter(el => el.name !== name);
      const currentElement = this.chartData.find(el => el.name === name);

      if (currentElement.show) {
        if (anotherElements.every(el => el.show === false)) {
          return;
        }

        return currentElement.show = false;
      }

      currentElement.show = true;
    },
  },
};
</script>

<style lang="sass" scoped>
  #canvasWrapper
    position: relative
    display: flex
    padding: 10px

    canvas
      width: 100%

    #controllerSwitch
      position: absolute
      top: 55%
      max-width: 180px
      left: 0
      right: 0
      margin: 0 auto
      text-align: center
      box-sizing: border-box
      border-radius: 25px
      background: rgba(30, 28, 47, 1)
      opacity: .5
      transition: all .18s ease-in

      &:hover
        opacity: 1
      a
        font-size: .75rem
        margin: 0 8px

        &.selected
          span
            color: #fff

        em
          display: inline-block
          vertical-align: middle
          width: 12px
          border-radius: 10px
          height: 12px
          margin: 0 4px 3px

        span
          color: #93939c

    #hint
      position: absolute
      font-size: .78rem
      text-align: left
      border-radius: 5px
      box-shadow: 0px -2px 5px 0px #2d2f48
      min-width: 205px
      z-index: 10

      @media (max-width: 767px)
        margin-left: .64rem
        margin-right: .64rem

      & > div
        position: relative
        padding: 0 20px

        &::before
          content: ''
          position: absolute
          left: calc(50% - 1.48rem)
          bottom: -0.88rem
          border-style: solid
          border-width: 1.24rem 1.24rem 0 1.24rem
          border-color: #1e1b2f transparent transparent

        & > div
          margin: 12px 0

          h4
            font-size: .9rem
            font-weight: 600
            margin-bottom: 2px

            em
              float: right

          span, em
            font-style: normal
            color: #fff
            font-weight: 600
            white-space: nowrap

          section
            display: flex

            span
              margin-right: 16px
            em
              margin-left: auto
</style>
